import React from 'react';
import styled, { keyframes } from 'styled-components';
import { AlertTriangle } from 'lucide-react';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

export default function ConfirmDialog({ isOpen, title, message, confirmLabel, cancelLabel, danger, loading, onConfirm, onCancel }) {
    if (!isOpen) return null;

    return (
        <Overlay onClick={onCancel} role='dialog' aria-modal='true' aria-labelledby='confirm-title'>
            <Dialog onClick={e => e.stopPropagation()}>
                <IconCircle $danger={danger}>
                    <AlertTriangle size={22} color={danger ? colors.danger : panelAccent(colors.occasionalPurple)} />
                </IconCircle>
                <Title id='confirm-title'>{title || 'Confirmar ação'}</Title>
                {message && <Message>{message}</Message>}
                <Actions>
                    <CancelBtn type='button' onClick={onCancel} disabled={loading}>
                        {cancelLabel || 'Cancelar'}
                    </CancelBtn>
                    <ConfirmBtn type='button' $danger={danger} onClick={onConfirm} disabled={loading}>
                        {loading ? 'Aguarde...' : (confirmLabel || 'Confirmar')}
                    </ConfirmBtn>
                </Actions>
            </Dialog>
        </Overlay>
    );
}

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { opacity: 0; transform: translateY(16px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); }`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(26, 26, 26, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 200;
    padding: 20px;
    animation: ${fadeIn} 0.15s ease;
`;

const Dialog = styled.div`
    background: #fff;
    border-radius: 16px;
    padding: 28px 24px 20px;
    max-width: 340px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
    animation: ${slideUp} 0.2s ease;
`;

const IconCircle = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: ${({ $danger }) => $danger ? `${colors.danger}12` : `${panelAccentAlpha(colors.occasionalPurple, '12')}`};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
`;

const Title = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 6px;
`;

const Message = styled.p`
    font-size: 14px;
    color: #666;
    margin: 0 0 4px;
    line-height: 1.4;
`;

const Actions = styled.div`
    display: flex;
    gap: 10px;
    width: 100%;
    margin-top: 20px;
`;

const CancelBtn = styled.button`
    flex: 1;
    padding: 12px;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    background: #fff;
    color: ${colors.nightDark};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
    &:hover { background: ${colors.neutralDark}; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ConfirmBtn = styled.button`
    flex: 1;
    padding: 12px;
    border-radius: 10px;
    border: none;
    background: ${({ $danger }) => $danger ? colors.danger : panelAccent(colors.occasionalPurple)};
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
    &:hover:not(:disabled) { opacity: 0.9; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
